import { toast } from 'react-toastify';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { API_ENDPOINT } from '../constants';
import { fetchWrapper } from '../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { FeatureFlagState } from './types';

const baseUrl = `${API_ENDPOINT}/feature_flags`;

const getFeatureFlags = createAsyncThunk(
  'featureFlags/getFeatureFlags',
  async ({ getAccessTokenSilently }: { getAccessTokenSilently: () => Promise<string> }) => {
    const response: Record<string, boolean> = await fetchWrapper.get({
      url: `${baseUrl}/`,
      getAccessTokenSilently,
    });
    return response;
  },
);

const setFeatureFlag = createAsyncThunk(
  'featureFlags/setFeatureFlag',
  async ({
    key,
    enabled,
    getAccessTokenSilently,
    getAdminActionPassword,
  }: {
    key: string;
    enabled: boolean;
    getAccessTokenSilently: () => Promise<string>;
    getAdminActionPassword: () => string;
  }) => {
    const response: { key: string; enabled: boolean } = await fetchWrapper.put(
      { url: `${baseUrl}/${key}`, getAccessTokenSilently },
      { enabled, password: getAdminActionPassword() },
    );
    return response;
  },
);

function createInitialState(): FeatureFlagState {
  return {
    flags: {},
    loading: false,
    error: null,
  };
}

function createExtraReducers(builder: ActionReducerMapBuilder<FeatureFlagState>) {
  builder
    .addCase(getFeatureFlags.pending, (state: FeatureFlagState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getFeatureFlags.fulfilled,
      (state: FeatureFlagState, action: PayloadAction<Record<string, boolean>>) => {
        state.flags = action.payload;
        state.loading = false;
      },
    )
    .addCase(getFeatureFlags.rejected, (state: FeatureFlagState, action) => {
      state.error = action.error.message ?? null;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'global-toast-error' });
    })

    .addCase(setFeatureFlag.pending, (state: FeatureFlagState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      setFeatureFlag.fulfilled,
      (state: FeatureFlagState, action: PayloadAction<{ key: string; enabled: boolean }>) => {
        state.flags[action.payload.key] = action.payload.enabled;
        state.loading = false;
      },
    )
    .addCase(setFeatureFlag.rejected, (state: FeatureFlagState, action) => {
      state.error = action.error.message ?? null;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'global-toast-error' });
    });
}

const slice = createSlice({
  name: 'featureFlags',
  initialState: createInitialState(),
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<FeatureFlagState>) =>
    createExtraReducers(builder),
});

export const featureFlagActions = { ...slice.actions, getFeatureFlags, setFeatureFlag };
export const featureFlagReducer = slice.reducer;
